import { EventPackage } from "../../models/AssignedSeating";
import { EventSeatmap, SeatmapSection } from "../../models/Event";
import { buildSeatFrames, SeatGeom } from "./canvas/seatGeometry";

/**
 * Stranded-seat ("orphan seat") detection for the purchase seatmap.
 *
 * A pick strands a seat when it chops a run of free seats into a run too short
 * for anyone else to buy — classically the lone seat left between two sold
 * ones. Instead of silently allowing that, the buyer is asked to take the
 * orphan(s) along; this module works out which seats those are.
 *
 * Adjacency comes from the rendered geometry rather than seat numbering, so
 * aisles, deactivated seats and curved/skewed rows break a run exactly where
 * the buyer sees a break on the map.
 */

/** Smallest run of free seats we're willing to leave behind in a row. */
export const MIN_CONTIGUOUS_SEATS = 2;

/**
 * A seat in the picked seat's row, carrying the two facts the check needs:
 * whether anyone could still buy it, and whether this buyer already holds it.
 */
export type StrandableSeat = {
    geom: SeatGeom;
    /** Purchasable right now (not sold, held, inactive or box-office only). */
    sellable: boolean;
    /** Already in this buyer's cart. */
    inCart: boolean;
};

/** A gap wider than this multiple of the row's typical pitch splits the row. */
const GAP_RATIO = 1.6;

const distance = (a: SeatGeom, b: SeatGeom) =>
    Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Splits a row into physically contiguous stretches: seats are ordered along
 * the row and cut wherever the space to the next seat is noticeably wider than
 * the row's typical pitch (an aisle, or a deactivated seat leaving a hole).
 */
export function splitContiguousRuns(
    row: StrandableSeat[],
): StrandableSeat[][] {
    if (row.length < 2) return row.length ? [row] : [];

    const ordered = [...row].sort(
        (a, b) => a.geom.x - b.geom.x || a.geom.y - b.geom.y,
    );
    const gaps = ordered
        .slice(1)
        .map((s, i) => distance(ordered[i].geom, s.geom));
    // Median pitch: robust to the handful of wide gaps we're looking for, and
    // to the slight per-seat variation of arced / skewed / coned rows.
    const pitch = [...gaps].sort((a, b) => a - b)[Math.floor(gaps.length / 2)];
    if (!pitch) return [ordered];

    const runs: StrandableSeat[][] = [[ordered[0]]];
    ordered.slice(1).forEach((s, i) => {
        if (gaps[i] > pitch * GAP_RATIO) runs.push([]);
        runs[runs.length - 1].push(s);
    });
    return runs;
}

/** Ids of seats sitting in a run of free seats shorter than `minRun`. */
function orphanIds(
    run: StrandableSeat[],
    free: boolean[],
    minRun: number,
): Set<number> {
    const ids = new Set<number>();
    let start = 0;
    for (let i = 0; i <= run.length; i++) {
        if (i < run.length && free[i]) continue;
        const length = i - start;
        if (length > 0 && length < minRun) {
            for (let j = start; j < i; j++) ids.add(run[j].geom.seat.id);
        }
        start = i + 1;
    }
    return ids;
}

/**
 * The seats `picks` would orphan. Runs that were already too short before the
 * pick are left alone — the buyer only answers for the gaps they create.
 */
export function findStrandedSeats(
    row: StrandableSeat[],
    picks: Set<number>,
    minRun: number = MIN_CONTIGUOUS_SEATS,
): SeatGeom[] {
    const stranded: SeatGeom[] = [];

    splitContiguousRuns(row).forEach((run) => {
        const before = run.map((s) => s.sellable && !s.inCart);
        const after = run.map(
            (s, i) => before[i] && !picks.has(s.geom.seat.id),
        );
        const wasOrphan = orphanIds(run, before, minRun);
        const isOrphan = orphanIds(run, after, minRun);

        run.forEach((s) => {
            const id = s.geom.seat.id;
            if (isOrphan.has(id) && !wasOrphan.has(id)) stranded.push(s.geom);
        });
    });

    return stranded;
}

/**
 * Seats of the ROW that each seat belongs to, keyed by seat id. Only ROW
 * sections are indexed: tables and free-placed MANUAL seats have no "seat
 * next to you" to strand.
 */
export type SeatRowIndex = Map<number, SeatGeom[]>;

export function buildSeatRowIndex(
    layout: EventSeatmap,
    eventPackage?: EventPackage,
): SeatRowIndex {
    const rowSectionIds = new Set<number>();
    const visit = (sec: SeatmapSection) => {
        if (sec.type === "ROW") rowSectionIds.add(sec.id);
        sec.sections.forEach(visit);
    };
    layout.seatmapTemplate.sections.forEach(visit);

    const bySection = new Map<number, SeatGeom[]>();
    buildSeatFrames(layout, eventPackage).forEach((f) =>
        f.seats.forEach((g) => {
            if (!rowSectionIds.has(g.sourceSectionId)) return;
            const list = bySection.get(g.sourceSectionId);
            if (list) {
                list.push(g);
            } else {
                bySection.set(g.sourceSectionId, [g]);
            }
        }),
    );

    const index: SeatRowIndex = new Map();
    bySection.forEach((seats) =>
        seats.forEach((g) => index.set(g.seat.id, seats)),
    );
    return index;
}

export type RemovalStrand = {
    /** Seats the removal would leave on their own. */
    stranded: SeatGeom[];
    /** Cart seats that have to come out too so that doesn't happen. */
    alsoRemove: SeatGeom[];
};

const NO_STRAND: RemovalStrand = { stranded: [], alsoRemove: [] };

/** Bounds of the free stretch containing `idx`. */
function stretchAround(free: boolean[], idx: number): [number, number] {
    let lo = idx;
    let hi = idx;
    while (lo - 1 >= 0 && free[lo - 1]) lo--;
    while (hi + 1 < free.length && free[hi + 1]) hi++;
    return [lo, hi];
}

/** Free seats immediately beyond `idx`, walking outwards in `step`. */
function freeBeyond(free: boolean[], idx: number, step: number): number {
    let count = 0;
    for (let i = idx + step; i >= 0 && i < free.length && free[i]; i += step) {
        count++;
    }
    return count;
}

/**
 * Giving a seat back strands it when the seats it frees up are too few for
 * anyone else to take — pulling one seat out of the middle of a bought-out
 * row, say. The remedy is to release a neighbouring cart seat as well, and
 * this works out which one: whichever side opens onto the most free space, so
 * the released seats merge into the block beside them rather than splitting
 * the buyer's own seats in two.
 *
 * Returns no strand when the removal is harmless *and* when nothing could fix
 * it (the seat has no cart neighbours left) — a buyer must always be able to
 * empty their cart.
 */
export function findRemovalStrand(
    row: StrandableSeat[],
    removals: Set<number>,
    minRun: number = MIN_CONTIGUOUS_SEATS,
): RemovalStrand {
    for (const run of splitContiguousRuns(row)) {
        const anchor = run.findIndex((s) => removals.has(s.geom.seat.id));
        if (anchor === -1) continue;

        const freeBefore = run.map((s) => s.sellable && !s.inCart);
        const released = new Set<number>(
            run
                .filter((s) => removals.has(s.geom.seat.id))
                .map((s) => s.geom.seat.id),
        );

        let stranded: SeatGeom[] | null = null;
        const alsoRemove: SeatGeom[] = [];

        for (let guard = 0; guard <= run.length; guard++) {
            const free = run.map(
                (s, i) => freeBefore[i] || released.has(s.geom.seat.id),
            );
            const [lo, hi] = stretchAround(free, anchor);
            if (hi - lo + 1 >= minRun) {
                return alsoRemove.length > 0
                    ? { stranded: stranded ?? [], alsoRemove }
                    : NO_STRAND;
            }

            if (stranded === null) {
                stranded = run.slice(lo, hi + 1).map((s) => s.geom);
            }

            const candidate = (i: number, step: number) =>
                i >= 0 &&
                i < run.length &&
                run[i].inCart &&
                !released.has(run[i].geom.seat.id)
                    ? 1 + freeBeyond(free, i, step)
                    : null;
            const leftScore = candidate(lo - 1, -1);
            const rightScore = candidate(hi + 1, 1);

            // Nothing left to release: let the removal through rather than
            // trapping the buyer with a seat they can't give back.
            if (leftScore === null && rightScore === null) return NO_STRAND;

            const takeLeft =
                rightScore === null ||
                (leftScore !== null && leftScore >= rightScore);
            const pulled = run[takeLeft ? lo - 1 : hi + 1];
            released.add(pulled.geom.seat.id);
            alsoRemove.push(pulled.geom);
        }
    }

    return NO_STRAND;
}
